import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPatients } from "../../api/services/clinic/patientManagement";
import Avatar from "../../components/Avatar";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import Pagination from "../../components/Pagination";
import PaitentActionPanel from "../../features/patient/PatientActionsPanel";
import StaffLayout from "../StaffLayout";

const PAGE_SIZE = 10;

export default function PatientManagement() {
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const navigate = useNavigate();

    const loadPatients = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await fetchPatients({ page, pageSize: PAGE_SIZE });
            setPatients(data.results || []);
            setTotalCount(data.count || 0);
            setTotalPages(data.total_pages || Math.ceil((data.count || 0) / PAGE_SIZE));
        } catch (err) {
            console.error(err);
            setPatients([]);
            setTotalCount(0);
            setTotalPages(0);
            setError(err?.message || "ไม่สามารถโหลดข้อมูลผู้ป่วยได้");
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        loadPatients();
    }, [loadPatients]);


    function formatIdNumber(id) {
        if (!id) return "-";
        if (id.length === 13) {
            return `${id[0]} ${id.slice(1, 5)} ${id.slice(5, 10)} ${id.slice(10, 12)} ${id[12]}`;
        }
        return id;
    }

    function formatPhoneNumber(phone) {
        if (!phone) return "-";
        if (phone.length === 10) {
            return `${phone.slice(0, 3)} ${phone.slice(3, 6)} ${phone.slice(6)}`;
        }
        return phone;
    }

    function buildHeaderIcon() {
        return <Icon name="user-group" size="xl" className='text-main-icon-system-description'></Icon>
    }

    function buildHeaderAction() {
        return <PaitentActionPanel onSuccess={loadPatients}></PaitentActionPanel>
    }

    function buildBody() {
        return <div className="flex flex-col gap-4">
            {error && (
                <div className="rounded-lg border border-danger-border bg-danger-background p-4 text-danger-default">
                    {error}
                </div>
            )}
            <div className="table-container">
                <table className="table">
                    <thead className="table-thead">
                        <tr>
                            <th className="table-th w-[112px] text-left">HN</th>
                            <th className="table-th w-[48px] text-left"></th>
                            <th className="table-th flex-1 text-left">ชื่อผู้ป่วย</th>
                            <th className="table-th w-[240px] text-left">บัตรประชาชน/พาสปอร์ต</th>
                            <th className="table-th w-[144px] text-left">โทรศัพท์</th>
                            <th className="table-th w-[200px] text-left">อายุ</th>
                            <th className="table-th w-[88px] text-left">เพศ</th>
                            <th className="table-th w-[160px] text-left">การดำเนินการ</th>
                        </tr>
                    </thead>
                    <tbody className="table-body">
                        {loading && (
                            <tr>
                                <td colSpan="8" className="table-td text-center align-middle">
                                    กำลังโหลดข้อมูล...
                                </td>
                            </tr>
                        )}
                        {!loading && patients.length === 0 && (
                            <tr>
                                <td colSpan="8" className="table-td text-center align-middle text-main-typography-system-description">
                                    ยังไม่มีข้อมูลผู้ป่วย
                                </td>
                            </tr>
                        )}
                        {!loading && patients.map((patient) => (
                            <tr className="table-row cursor-pointer"
                                key={patient.id}
                                onClick={() => onClickViewPatient(patient)}>
                                <td className="table-td text-left align-middle">
                                    <span className="table-td-value">{patient.hn || "-"}</span>
                                </td>

                                <td className="p-3 align-middle">
                                    <div className="flex items-center justify-center">
                                        {patient.photo ? (
                                            <Avatar
                                                picture={patient.photo}
                                                shape="square"
                                                size="lg">
                                            </Avatar>
                                        ) : (
                                            <Icon
                                                name="user-circle"
                                                size="lg"
                                                className="text-main-icon-system-caption"
                                                wrapperClass="rounded-sm bg-main-background-system-default-dim">
                                            </Icon>
                                        )}
                                    </div>
                                </td>
                                <td className="table-td align-middle text-left">
                                    <span className="table-td-value">{patient.patient_name || "-"}</span>
                                </td>
                                <td className="table-td align-middle text-left">
                                    <span className="table-td-value">{formatIdNumber(patient.id_number)}</span>
                                </td>
                                <td className="table-td text-left align-middle">
                                    <span className="table-td-value">{formatPhoneNumber(patient.phone_number)}</span>
                                </td>
                                <td className="table-td text-left align-middle">
                                    <span className="table-td-value">{patient.age || "-"}</span>
                                </td>
                                <td className="table-td text-left align-middle">
                                    <span className="table-td-value">{patient.gender || "-"}</span>
                                </td>
                                <td className="align-middle">
                                    <div className="flex items-center justify-center gap-3">
                                        <Button
                                            id="edit-patient"
                                            hierarchy="tertiary"
                                            size="md"
                                            icon="pencil-alt"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onClickEditPatient(patient);
                                            }} >
                                        </Button>
                                        <Button
                                            id="view-patient"
                                            hierarchy="tertiary"
                                            size="md"
                                            icon="eye"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                onClickViewPatient(patient);
                                            }}>
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>;
    }

    function onClickEditPatient(patient) {
        if (!patient?.id) return;
        navigate(`/patient-management/profile/${patient.id}`, { state: { showModal: true } });
    }

    function handlePageChange(newPage) {
        if (newPage < 1) return;
        if (totalPages && newPage > totalPages) return;
        setPage(newPage);
    }

    function onClickViewPatient(patient) {
        if (!patient?.id) return;
        navigate(`/patient-management/profile/${patient.id}`);
    }

    function buildFooter() {
        const hasPatients = totalCount > 0;
        const startIndex = hasPatients ? (page - 1) * PAGE_SIZE + 1 : 0;
        const endIndex = hasPatients ? startIndex + patients.length - 1 : 0;
        return (
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-main-typography-system-description text-body-base">
                    แสดง {hasPatients ? `${startIndex}-${endIndex}` : 0} จาก {totalCount} รายการ
                </p>
                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    maxVisible={5}
                    onPageChange={handlePageChange}
                />
            </div>
        );
    }

    return (
        <StaffLayout
            pageTitle="การจัดการผู้ป่วย - คลินิกเวชกรรม ใกล้หมอ"
            headerIcon={buildHeaderIcon()}
            headerTitle="การจัดการผู้ป่วย"
            contentHeaderTitle="รายชื่อผู้ป่วย"
            contentHeaderAction={buildHeaderAction()}
            footer={buildFooter()}
        >
            {buildBody()}
        </StaffLayout>
    );
}
